import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityAction, AuditActorKind, type ActivityLogToken } from '@sleed/bizcommerce-api';
import { CircleHelp, CirclePlus, LogIn, LogOut, Pencil, ShieldAlert, Trash2, Undo2, UserCheck } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { formatDateTime, formatRelativeTime, toIsoUtc } from '~/lib/format-date';

/** How many field names fit in a grid cell before the rest collapse into a `+N` chip. */
const VISIBLE_CHIPS = 2;

/** Stands for a value that is absent — not the same thing as a value that was cleared to empty. */
const ABSENT = '—';

/** Relative labels stop here: past a week the exact date is what an auditor cites, so show it instead. */
const RELATIVE_CUTOFF_MS = 7 * 86400 * 1000;

/**
 * Per-action icon and badge colour. Semantic rather than decorative: the colour is the fastest way to spot
 * a deletion or a failed login while scrolling, so destructive and privilege-elevating actions are the loud
 * ones and routine sessions stay quiet.
 */
const ACTION_META: Record<number, { icon: typeof Pencil; className: string }> = {
	[ActivityAction.Insert]: { icon: CirclePlus, className: 'bg-success-subtle text-success-fg border-success-border' },
	[ActivityAction.Update]: { icon: Pencil, className: 'bg-info-subtle text-info-fg border-info-border' },
	[ActivityAction.Delete]: { icon: Trash2, className: 'bg-danger-subtle text-danger-fg border-danger-fg/30' },
	[ActivityAction.Restore]: { icon: Undo2, className: 'bg-warning-subtle text-warning-fg border-warning-border' },
	[ActivityAction.Login]: { icon: LogIn, className: 'bg-muted text-foreground-muted border-border' },
	[ActivityAction.Logout]: { icon: LogOut, className: 'bg-muted text-foreground-muted border-border' },
	[ActivityAction.LoginFailed]: { icon: ShieldAlert, className: 'bg-danger-subtle text-danger-fg border-danger-fg/30' },
	[ActivityAction.ImpersonationStart]: { icon: UserCheck, className: 'bg-foreground/10 text-foreground border-foreground/20' }
};

/** An action the UI does not know yet still renders — as its raw value, not as a blank cell. */
const UNKNOWN_ACTION_META = { icon: CircleHelp, className: 'bg-muted text-foreground-muted border-border' };

/**
 * AuditActorKind values worth naming in the UI. `User` is the ordinary back-office actor and stays
 * unlabelled — saying "User" on every row of an admin activity log is noise. The rest only appear on
 * explicitly logged actions, where knowing a scheduled job (not a person) did it is the whole point.
 */
const ACTOR_KIND_KEYS: Record<number, string> = {
	[AuditActorKind.System]: 'system',
	[AuditActorKind.Customer]: 'customer',
	[AuditActorKind.CustomerEmployee]: 'customerEmployee',
	[AuditActorKind.SalesPerson]: 'salesPerson',
	[AuditActorKind.ApiKey]: 'apiKey'
};

/**
 * The action as a colour-coded badge. Rendered inside the entity cell on the global grid rather than as its
 * own column — "what happened" and "what it happened to" are one fact, and splitting them cost a column for
 * one short word. The per-entity history keeps it standalone: there is no entity cell to ride along with.
 */
export function ActionBadge({ action, label }: { action?: number; label: string }) {
	const meta = action === undefined ? UNKNOWN_ACTION_META : (ACTION_META[action] ?? UNKNOWN_ACTION_META);
	const Icon = meta.icon;

	return (
		<Badge variant="outline" className={meta.className}>
			<Icon />
			{label}
		</Badge>
	);
}

/**
 * When the action happened. Recent rows read as relative time — the question asked of an audit log is
 * almost always "how long ago" — and anything older than a week as its absolute date. The full timestamp is
 * always on the tooltip, so precision is one hover away either way.
 */
export function WhenCell({ occurredOn }: { occurredOn?: ActivityLogToken['occ'] }) {
	const { i18n } = useTranslation();

	// The API returns naive-UTC strings; without this every label would be out by the viewer's offset,
	// which a relative phrase makes glaring ("in 3 hours" for an action that just happened).
	const iso = toIsoUtc(occurredOn);

	if (!iso) return <span className="text-xs text-foreground-muted">{ABSENT}</span>;

	const absolute = formatDateTime(iso);
	const isRecent = Date.now() - new Date(iso).getTime() < RELATIVE_CUTOFF_MS;

	return (
		<span className="text-xs whitespace-nowrap text-foreground-muted" title={absolute}>
			{isRecent ? formatRelativeTime(iso, i18n.language) : absolute}
		</span>
	);
}

/**
 * Who acted, and under which role. One column because the two are read together and never apart: a role on
 * its own identifies nobody, and a name without the role it held at the time is what this log exists to
 * avoid. The role is the one recorded on the entry — not the actor's current role, which a rename or a
 * reassignment would otherwise rewrite.
 */
export function ActorCell({ row }: { row: ActivityLogToken }) {
	const { t } = useTranslation();

	const kindKey = row.akind === undefined ? undefined : ACTOR_KIND_KEYS[row.akind];
	const kind = kindKey ? t(`system.activityLog.actorKinds.${kindKey}`) : null;

	return (
		<span className="flex min-w-0 flex-col">
			<span className="truncate text-sm font-medium text-foreground">{row.anm || ABSENT}</span>
			<span className="truncate text-[11px] text-foreground-subtle">
				{/* A non-User actor cannot hold a role, so the kind only ever adds to a role that exists —
				    it never silently replaces one. */}
				{[row.arnm || (kind ? null : ABSENT), kind].filter(Boolean).join(' · ')}
			</span>
		</span>
	);
}

/**
 * What the action was about: the action badge and entity type on the first line, the record's identity on
 * the second. Folds the former entity / id / key / description columns, which all answered one question and
 * together took half the grid.
 */
export function EntityCell({ row, actionLabel }: { row: ActivityLogToken; actionLabel: string }) {
	const identity = [row.edn, row.ekey, row.eid ? `#${row.eid}` : null].filter(Boolean).join(' · ');

	return (
		<span className="flex min-w-0 flex-col gap-0.5">
			<span className="flex min-w-0 items-center gap-2">
				<ActionBadge action={row.act} label={actionLabel} />
				<span className="truncate text-sm text-foreground">{row.enm || ABSENT}</span>
			</span>
			{identity ? <span className="truncate text-[11px] text-foreground-subtle">{identity}</span> : null}
		</span>
	);
}

/** Export value of a whole cell. Shared shape with the legacy admin's export so the two read the same. */
export function formatChangedFields(fields: string[] | undefined): string {
	return (fields ?? []).join(', ');
}

interface ChangedFieldsCellProps {
	fields?: string[];
	/** Which record the change was on — appended to the dialog title. */
	subject?: string | null;
}

/**
 * Which fields changed: the first few as chips in the cell, the whole list in a dialog on click (BW-4412).
 * Names only — before/after values are deliberately not recorded, so child rows arrive as the parent's
 * logical field (`Prices`, `Stock`) and an insert or a deletion carries no field list at all.
 */
export function ChangedFieldsCell({ fields, subject }: ChangedFieldsCellProps) {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);

	const entries = fields ?? [];

	if (entries.length === 0) return <span className="text-xs text-foreground-muted">{ABSENT}</span>;

	const visible = entries.slice(0, VISIBLE_CHIPS);
	const hidden = entries.length - visible.length;

	return (
		<>
			<button
				type="button"
				className="flex min-w-0 items-center gap-1 text-left"
				title={t('system.activityLog.changedFields.open')}
				// Defensive: SystemGrid rows are not clickable today, but a cell that opens a dialog should not
				// depend on that staying true.
				onClick={event => {
					event.stopPropagation();
					setOpen(true);
				}}>
				{visible.map(field => (
					<Badge key={field} variant="secondary" className="max-w-[110px] truncate">
						{field}
					</Badge>
				))}
				{hidden > 0 ? <Badge variant="outline">+{hidden}</Badge> : null}
			</button>
			{/* Mounted only while open: one grid page holds up to 500 of these cells. */}
			{open ? <ChangedFieldsDialog open onOpenChange={setOpen} fields={entries} subject={subject} /> : null}
		</>
	);
}

interface ChangedFieldsDialogProps extends Required<Pick<ChangedFieldsCellProps, 'fields'>> {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	subject?: string | null;
}

/** The full list of field names. Wrapped chips rather than a one-column table: there is no second column. */
function ChangedFieldsDialog({ open, onOpenChange, fields, subject }: ChangedFieldsDialogProps) {
	const { t } = useTranslation();

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
				<DialogHeader>
					<DialogTitle>
						{t('system.activityLog.changedFields.title')}
						{subject ? ` — ${subject}` : ''}
					</DialogTitle>
				</DialogHeader>
				<div className="flex flex-wrap gap-1.5">
					{fields.map(field => (
						<Badge key={field} variant="secondary">
							{field}
						</Badge>
					))}
				</div>
			</DialogContent>
		</Dialog>
	);
}

/**
 * The role's permissions as they stood at action time. Display only — a snapshot exists so an entry stays
 * interpretable after the role is edited, and the column is too narrow to show a dozen names inline.
 */
export function ActorPermissionsCell({ permissions }: { permissions?: string[] }) {
	const { t } = useTranslation();

	const names = permissions ?? [];

	if (names.length === 0) return <span className="text-xs text-foreground-muted">{ABSENT}</span>;

	return (
		<Popover>
			<PopoverTrigger asChild>
				<button
					type="button"
					className="truncate text-left text-xs text-foreground-muted underline decoration-dotted"
					onClick={event => event.stopPropagation()}>
					{t('system.activityLog.permissionsCount', { count: names.length })}
				</button>
			</PopoverTrigger>
			<PopoverContent className="w-64">
				<div className="flex flex-wrap gap-1">
					{names.map(name => (
						<Badge key={name} variant="secondary">
							{name}
						</Badge>
					))}
				</div>
			</PopoverContent>
		</Popover>
	);
}
